import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useActivityLogger } from "./hooks/useActivity";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, user, loading: authLoading } = useAuth();
  const { logUserActivity } = useActivityLogger();
  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      navigate("/homelogin", { replace: true });
    }
    logUserActivity(
      "user",
      "view_login_page",
      "Login",
      "User viewed login page"
    );
  }, [user, authLoading, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!username || !password) {
      setErrorMessage("Harap isi semua field.");
      return;
    }

    setLoading(true);
    try {
      const result = await login(username, password);

      if (result.success) {
        // Navigation will be handled by the useEffect above
        logUserActivity(
          "user",
          "login",
          "Login",
          `User logged in with username: ${username}`
        );
        navigate("/homelogin", { replace: true });
      } else {
        if (result.error === "invalid credentials"){
          setErrorMessage("Username atau password salah. Silakan coba lagi.");
        } 
        else if (result.error === "user not found") {
          setErrorMessage("Pengguna tidak ditemukan. Silakan daftar terlebih dahulu.");
        } else {
          setErrorMessage("Terjadi kesalahan saat login. Silakan coba lagi.");
          console.error("Login failed:", result.error);
        }
      }
    } catch (err) {
      console.error("Login error:", err);
      setErrorMessage("Login gagal. Silakan coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth status
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <p className="ml-2">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header with back button */}
      <div className="flex items-center justify-between px-4 py-4 pt-8">
        <button
          onClick={() => navigate("/")}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          disabled={loading}
        >
          <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
        </button>
        <div className="flex-1" /> {/* Spacer */}
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto pb-32"> {/* Added padding bottom for fixed buttons */}
        <div className="flex flex-col items-center px-4 py-4">
          {/* Logo section */}
          <div className="mt-8 mb-12 text-center">
            <img src="logohallostroke.png" alt="Hallo Stroke Logo" className="mx-auto w-64" />
          </div>

          {/* Form section */}
          <div className="w-full max-w-md px-4">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Username
                </label>
                <input
                  type="text"
                  placeholder="Masukkan username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Masukkan password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 pr-10"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-3 text-gray-500"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {errorMessage && (
                  <p className="text-red-500 text-sm mt-1">{errorMessage}</p>
                )}
              </div>
            </form>
            <div className="text-right mt-2">
              <Link to="/forgot-password" className="text-blue-500 hover:underline text-sm">
                Lupa password?
              </Link>
            </div>
          </div>

          {/* Add some extra space at the bottom */}
          <div className="h-16"></div>
        </div>
      </div>

      {/* Fixed bottom buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white px-8 pb-6 pt-4 shadow-lg border-t border-gray-100">
        <div className="w-full max-w-md mx-auto">
          <button
            onClick={handleLogin}
            type="button"
            className="w-full bg-blue-500 text-white py-2 rounded-md hover:bg-blue-600 transition mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? "Masuk..." : "Masuk"}
          </button>

          <div className="flex items-center justify-between my-2">
            <hr className="w-full border-gray-300" />
            <span className="mx-2 text-gray-500 text-sm">atau</span>
            <hr className="w-full border-gray-300" />
          </div>

          <Link to="/register">
            <button
              className="w-full border border-blue-500 text-blue-500 py-2 rounded-md text-base font-medium hover:bg-blue-50 transition mt-3 disabled:opacity-50"
              disabled={loading}
            >
              Buat Akun
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
}
